// SPDX-License-Identifier: AGPL-3.0-or-later

import Authentication from '@app/features/auth/state/Authentication';
import {FeedUserPostsModal} from '@app/features/feed/components/FeedUserPostsModal';
import {isFeedEnabled} from '@app/features/feed/utils/FeedUtils';
import {FollowListModal} from '@app/features/follow/components/FollowListModal';
import {FOLLOW_COUNTS_DESCRIPTOR} from '@app/features/follow/components/ProfileFollowActions';
import {getFollowControls, getFollowProfile} from '@app/features/follow/utils/FollowProfileUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/user_profile_modal/ProfileStatLinks.module.css';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {NewspaperIcon, UsersIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

const FOLLOWERS_LABEL_DESCRIPTOR = msg({
	message: '{count, plural, one {follower} other {followers}}',
	comment:
		'Word after the follower count beside the username on the profile, as in "12 followers"; the number is shown before it. Opens the follower list.',
});
const POSTS_DESCRIPTOR = msg({
	message: 'Posts',
	comment:
		'Visible text of the profile button beside the username that opens the feed posts of the user. Keep it concise.',
});

// Micup: the follower count and Posts beside the username in the profile modal, as thin outlined chips.
export const ProfileStatLinks = observer(({userId}: {userId: string}) => {
	const {i18n} = useLingui();
	const user = Users.getUser(userId);
	const profile = getFollowProfile(userId);
	if (!user) return null;
	const showCounts = profile != null && getFollowControls(profile, user, Authentication.currentUserId).showCounts;
	const showPosts = isFeedEnabled() && !user.bot && !user.system;
	if (!showCounts && !showPosts) return null;
	const followerCount = profile?.followerCount ?? 0;
	const followingCount = profile?.followingCount ?? 0;
	const openFollowers = () => {
		ModalCommands.push(
			modal(() => (
				<FollowListModal
					userId={userId}
					initialKind="followers"
					data-flx="user.profile-stat-links.open-followers.follow-list-modal"
				/>
			)),
		);
	};
	const openPosts = () => {
		ModalCommands.push(
			modal(() => <FeedUserPostsModal userId={userId} data-flx="user.profile-stat-links.feed-user-posts-modal" />),
		);
	};
	return (
		<div className={styles.root} data-flx="user.profile-stat-links.root">
			{showCounts && (
				<FocusRing offset={-2} data-flx="user.profile-stat-links.focus-ring">
					<Tooltip
						text={i18n._(FOLLOW_COUNTS_DESCRIPTOR, {followerCount, followingCount})}
						data-flx="user.profile-stat-links.tooltip"
					>
						<button
							type="button"
							className={styles.link}
							onClick={openFollowers}
							data-flx="user.profile-stat-links.link.followers"
						>
							<UsersIcon weight="bold" className={styles.icon} data-flx="user.profile-stat-links.users-icon" />
							<span data-flx="user.profile-stat-links.count">
								<span className={styles.number} data-flx="user.profile-stat-links.number">
									{i18n.number(followerCount, {notation: 'compact'})}
								</span>{' '}
								{i18n._(FOLLOWERS_LABEL_DESCRIPTOR, {count: followerCount})}
							</span>
						</button>
					</Tooltip>
				</FocusRing>
			)}
			{showPosts && (
				<FocusRing offset={-2} data-flx="user.profile-stat-links.focus-ring--2">
					<button
						type="button"
						className={styles.link}
						onClick={openPosts}
						data-flx="user.profile-stat-links.link.posts"
					>
						<NewspaperIcon weight="bold" className={styles.icon} data-flx="user.profile-stat-links.newspaper-icon" />
						{i18n._(POSTS_DESCRIPTOR)}
					</button>
				</FocusRing>
			)}
		</div>
	);
});
